import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { useBrandContext } from './brandShared';

// Tab "Manual": punto de entrada al Manual de marca descargable. El documento se
// renderiza en una ruta standalone (sin chrome de la app) para imprimir/guardar
// PDF limpio; acá solo explicamos y abrimos esa ruta en una pestaña nueva.

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.surface};
  max-width: 640px;
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-bottom: 6px;
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Bullets = styled.ul`
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  padding-left: 18px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.7;
`;

const DirtyHint = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: #D97706;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  align-items: center;
`;

const ShareHint = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.md};
  line-height: 1.6;
`;

export function BrandTabManual() {
  const { workspaceId, form } = useBrandContext();
  const [copied, setCopied] = useState(false);

  const manualUrl = workspaceId ? `${window.location.origin}/workspace/${workspaceId}/brand/manual` : '';

  const open = () => {
    if (!manualUrl) return;
    window.open(manualUrl, '_blank', 'noopener');
  };

  const copyLink = async () => {
    if (!manualUrl) return;
    try {
      await navigator.clipboard.writeText(manualUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard bloqueado (permisos/contexto inseguro): no rompemos la UI.
    }
  };

  return (
    <Card>
      <Title>Brand manual</Title>
      <Desc>
        Generate a polished document with everything you have, presented as a brand manual and reference for
        creating social media ads. It opens in a clean view: from there you <strong>print or save it as a
        PDF</strong> with your browser's dialog.
      </Desc>
      <Bullets>
        <li>Essence, positioning, audience and ideal customer</li>
        <li>Voice and tone — what to say and what to avoid</li>
        <li>Color palette and typography</li>
        <li>Content topics + recommended mix</li>
        <li>Guide for applying it to social media ads</li>
      </Bullets>
      <Actions>
        <Button onClick={open}>Open manual / Download PDF</Button>
        <Button $variant="outline" onClick={copyLink}>{copied ? 'Link copied ✓' : 'Copy public link'}</Button>
      </Actions>
      <ShareHint>
        The link is <strong>public</strong> — anyone you share it with can view this manual without logging in.
      </ShareHint>
      {form.isDirty && (
        <DirtyHint>● You have unsaved changes — the manual uses the last saved version. Save first to see them reflected.</DirtyHint>
      )}
    </Card>
  );
}
